/**
 * Admin Resource Types
 * Type definitions for resource content management in admin portal
 * 
 * TODO: STATUS FIELD DATABASE MIGRATION REQUIRED
 * The Resource table currently does NOT have a 'status' field in the database.
 * The status is derived from publishedAt and deletedAt fields in the frontend.
 * 
 * Once the database migration adds the status column:
 * 1. Change AdminResource.status from optional to required
 * 2. Update backend service to include status in queries
 * 3. Remove status derivation logic from components
 */

/**
 * Resource status enum
 * TODO: This will match the backend enum once status field is added to DB
 */
export type ResourceStatus = 'published' | 'draft' | 'archived';

/**
 * Resource type (Article, Video, Guide, etc.)
 */
export type ResourceType = {
  id: string;
  name: string;
};

/**
 * Resource category (nested structure from API)
 * TODO: API returns { category: { id, name } } not { id, name }
 */
export type ResourceCategory = {
  category: {
    id: string;
    name: string;
  };
};

/**
 * Resource tag (nested structure from API)
 * TODO: API returns { tag: { id, name } } not { id, name }
 */
export type ResourceTag = {
  tag: {
    id: string;
    name: string;
  };
};

/**
 * Resource author (user)
 */
export type ResourceAuthor = {
  id: string;
  name: string;
};

/**
 * Resource author (provider/pro) as returned by the admin APIs.
 * The company name is nested under the SP/FA relation (not flattened).
 */
export type ResourceAuthorPro = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  serviceProvider?: { companyName: string | null } | null;
  financialAdvisor?: { companyName: string | null } | null;
};

/**
 * Resolve a display name for a provider/pro author from the nested API shape.
 * Prefers the SP/FA company name, then the person's full name.
 */
export function getAuthorProDisplayName(
  authorPro: ResourceAuthorPro | null | undefined,
): string {
  if (!authorPro) return '';
  const company =
    authorPro.serviceProvider?.companyName || authorPro.financialAdvisor?.companyName;
  const fullName = [authorPro.firstName, authorPro.lastName].filter(Boolean).join(' ');
  return company || fullName || '';
}

/**
 * Resource entity as returned from admin APIs
 */
export type AdminResource = {
  id: string;
  title: string;
  content: string;
  seoUrl: string;
  seoTitle: string | null;
  seoDescription: string | null;
  
  // Type and category
  type: ResourceType;
  typeId: string;
  categories: ResourceCategory[];
  tags: ResourceTag[];
  
  // Status and metadata
  status?: ResourceStatus; // TODO: Make required when status field is added to DB
  isFeatured: boolean;
  isFeaturedForPro: boolean;
  isBquestRecommended?: boolean; // Feature 083: admin-set bQuest Recommended Resource

  // Author info.
  // Legacy single-author fields (kept for backward compat) + the multi-author
  // junction links (feature 082). authorLinks/authorProLinks are the source of
  // truth when present; the singular fields mirror the first of each.
  author: ResourceAuthor | null;
  authorId: string | null;
  authorPro: ResourceAuthorPro | null; // TODO: API uses 'authorPro', not just authorProId
  authorProId: string | null;
  authorLinks?: Array<{ author: ResourceAuthor }>;
  authorProLinks?: Array<{ authorPro: ResourceAuthorPro }>;
  
  // Additional metadata
  affiliateLink: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null; // TODO: Used to derive "archived" status temporarily
  
  // Files
  files?: AdminResourceFile[];

  // Computed fields for display
  viewsCount?: number;
  categoryNames?: string;
  tagNames?: string;
};

/**
 * Resource file entity
 */
export type AdminResourceFile = {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: 'IMAGE' | 'AUDIO' | 'VIDEO';
  mimeType: string;
  fileSize?: number;
  isPrimary?: boolean;
  sortOrder?: number;
};

/**
 * Uploaded media file metadata sent to the backend (S3 upload result).
 */
export type ResourceMediaPayload = {
  fileUrl: string;
  fileName: string;
  mimeType: string;
  fileSize?: number;
};

/**
 * Create resource payload (matches backend CreateResourceAdminDto)
 */
export type CreateResourcePayload = {
  title: string;
  content: string;
  seoUrl: string;
  seoTitle?: string;
  seoDescription?: string;
  typeId: string;
  publishedAt?: string;
  isFeatured?: boolean;
  isFeaturedForPro?: boolean;
  isBquestRecommended?: boolean;
  affiliateLink?: string;
  /** @deprecated single external author — prefer authorIds. */
  authorId?: string;
  /** @deprecated single SP/FA author — prefer authorProIds. */
  authorProId?: string;
  authorIds?: string[];
  authorProIds?: string[];
  categoryIds?: string[];
  tagIds?: string[];
  coverImage?: ResourceMediaPayload | null;
  videoFile?: ResourceMediaPayload | null;
};

/**
 * Update resource payload (all fields optional, matches backend UpdateResourceAdminDto)
 */
export type UpdateResourcePayload = {
  title?: string;
  content?: string;
  seoUrl?: string;
  seoTitle?: string;
  seoDescription?: string;
  typeId?: string;
  publishedAt?: string | null;
  isFeatured?: boolean;
  isFeaturedForPro?: boolean;
  isBquestRecommended?: boolean;
  affiliateLink?: string;
  /** @deprecated single external author — prefer authorIds. */
  authorId?: string;
  /** @deprecated single SP/FA author — prefer authorProIds. */
  authorProId?: string;
  authorIds?: string[];
  authorProIds?: string[];
  categoryIds?: string[];
  tagIds?: string[];
  coverImage?: ResourceMediaPayload | null;
  videoFile?: ResourceMediaPayload | null;
  status?: ResourceStatus;
};

/**
 * Resource filter options for list queries
 */
export type ResourceFilters = {
  status?: ResourceStatus;
  typeId?: string;
  categoryId?: string;
  tagId?: string;
  isFeatured?: boolean;
  isFeaturedForPro?: boolean;
  isBquestRecommended?: boolean;
  authorId?: string;
  authorProId?: string;
  publishedAfter?: string;
  publishedBefore?: string;
};

export type SortDirection = 'asc' | 'desc';

export type ResourceSortField = 'title' | 'type' | 'status' | 'createdAt' | 'updatedAt';

export type ResourceListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: ResourceSortField;
  sortOrder?: SortDirection;
  status?: ResourceStatus;
  typeId?: string;
  categoryId?: string;
  tagId?: string;
  isFeatured?: boolean;
  isFeaturedForPro?: boolean;
  isBquestRecommended?: boolean;
  authorId?: string;
  authorProId?: string;
  publishedAfter?: string;
  publishedBefore?: string;
};

/**
 * Admin resource type with isActive and resource count
 */
export type AdminResourceType = {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { resources: number };
};

/**
 * Admin resource tag with isActive and resource count
 */
export type AdminResourceTag = {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  _count?: { resources: number };
};

/**
 * Admin resource author with resource count
 */
export type AdminResourceAuthorItem = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  _count?: { resources: number };
};

export type CreateResourceTypePayload = {
  name: string;
  description?: string;
  isActive?: boolean;
};

export type UpdateResourceTypePayload = {
  id: string;
  name?: string;
  description?: string;
  isActive?: boolean;
};

export type CreateResourceTagPayload = {
  name: string;
  isActive?: boolean;
};

export type UpdateResourceTagPayload = {
  id: string;
  name?: string;
  isActive?: boolean;
};

export type CreateResourceAuthorPayload = {
  name: string;
};

export type UpdateResourceAuthorPayload = {
  id: string;
  name?: string;
};
